import { inject, Injectable } from '@angular/core';
import { BookingPosition, BookingResult, StockSourceType } from '../types';
import { BookingOrchestratorService } from './booking-orchestrator.service';
import { BookingRequest } from '../builder';
import { Validation } from '../validation/validation';
import { BookingReference } from '../util/booking-reference';
import { BookingTransaction } from '../transaction/booking-transaction';

/** Quelle einer Füllposition: KFZ oder Bufferstock (nicht Maschine). */
export type MachineFillSource = StockSourceType.CAR | StockSourceType.BUFFERSTOCK;

/** Eine Auftragsposition mit der je Position gewählten Quelle. */
export interface SourcedFillPosition {
  /** Füllmenge der Position (gewählte Quelle -> Maschine). */
  position: BookingPosition;
  /** Quelle dieser Position (KFZ oder Bufferstock). */
  source: MachineFillSource;
  /** Rücknahme Maschine -> gewählte Quelle (optional; 0/undefined = keine). */
  takeBack?: number;
  /** Verschrottung aus der Maschine (optional; quell-unabhängig). */
  disposal?: number;
}

/** Maschine sowie Quell-IDs (vom Aufrufer aufgelöst) für die Befüllung. */
export interface MachineFillTargets {
  /** ID der zu befüllenden Maschine (Buchungsziel). */
  machineId: number;
  /** ID des angemeldeten KFZ; fehlt in der BufferstockOnly-Welt. */
  carId?: number;
  /** StockObject-ID des Maschinen-Bufferstocks; `undefined` = keiner zugeordnet. */
  bufferstockId?: number;
  /** Beschreibung, die an alle Bewegungen der Buchung durchgereicht wird. */
  description?: string;
  /**
   * ID des Befüll-Auftrags. Dient als Transaction-ID, unter der alle Bewegungen
   * dieser Befüllung für einen möglichen Auftrags-Rollback gebündelt werden.
   */
  orderId: number;
  /**
   * Auftragsnummer des Befüll-Auftrags. Wird zur `booking_reference`
   * (`Order <Nr>`) aller Bewegungen dieser Befüllung, damit die Büroware sie
   * demselben Auftrag zuordnen kann.
   */
  orderNumber: string;
}

/** Zuletzt gebuchter Stand einer Position (Basis der nächsten Differenz). */
export interface FillSnapshotEntry {
  amount: number;
  takeBack: number;
  disposal: number;
}

/**
 * Zuletzt gebuchter Stand je Position, Schlüssel `${article_id}:${source}`.
 * Die Buchung stellt gegen diesen Stand die Differenz her; ein unveränderter
 * Stand ergibt Differenz 0 und damit keine Bewegung.
 */
export type FillSnapshot = ReadonlyMap<string, FillSnapshotEntry>;

/**
 * `snapshot` = zuletzt gebuchter Stand je Position nach diesem Lauf. Beim
 * nächsten `book()` (erneutes Speichern, auch nach Modal-Schließen) dient er als
 * Basis der Differenz, damit nur die Änderung gebucht wird.
 */
export interface MachineFillBookingResult extends BookingResult {
  /** Fortgeschriebener Stand je Position für die nächste Differenz-Buchung. */
  snapshot: FillSnapshot;
}

/** Differenz einer Position gegen den zuletzt gebuchten Stand. */
interface PositionDelta {
  item: SourcedFillPosition;
  /** Schlüssel im Snapshot: `${article_id}:${source}`. */
  key: string;
  /** Änderung des Füll-Nettos (`fill - takeBack`) gegen den gebuchten Stand. */
  deltaNet: number;
  /** Änderung der Verschrottung gegen den gebuchten Stand. */
  deltaDisposal: number;
  fromCar: boolean;
}

/** Eine aus einer Positions-Differenz abgeleitete Bewegung. */
interface PositionMovement {
  buildRequest: () => BookingRequest;
  /** Schreibt den erfolgreich gebuchten Wert in den Snapshot fort. */
  commit: (snapshot: Map<string, FillSnapshotEntry>) => void;
}

/**
 * Bucht die Füllpositionen eines Auftrags auf das Maschinenlager, je Position
 * aus ihrer Quelle (KFZ oder Bufferstock). Gebucht wird die DIFFERENZ gegen den
 * zuletzt gebuchten Stand: eine Mengenkorrektur (5 -> 3) bucht das Delta (-2),
 * ein unverändertes erneutes Speichern (Differenz 0) bucht nichts. Erst alle
 * Bewegungen validieren, nur bei null Blockern buchen - so fällt eine
 * Überbuchung vor der ersten Buchung auf, nicht mitten in einer Teilbuchung.
 */
@Injectable({ providedIn: 'root' })
export class MachineFillBookingService {
  private readonly bookingService = inject(BookingOrchestratorService);

  /**
   * Bucht die Differenz der Füllpositionen auf das Maschinenlager.
   *
   * @param sourced Positionen mit je gewählter Quelle, in gelisteter Reihenfolge
   * @param targets Maschine, KFZ und (optional) Bufferstock der Buchung
   * @param previous Zuletzt gebuchter Stand je Position (Basis der Differenz);
   *   leer bei der ersten Buchung.
   * @returns success, wenn alle Differenzen gebucht wurden; sonst das erste
   *   Failure. `snapshot` trägt den fortgeschriebenen Stand (für die nächste
   *   Differenz), inklusive der bis zu einem Teilfehler gebuchten Positionen.
   */
  public async book(
    sourced: SourcedFillPosition[],
    targets: MachineFillTargets,
    previous: FillSnapshot = new Map(),
  ): Promise<MachineFillBookingResult> {
    const snapshot = new Map<string, FillSnapshotEntry>(previous);

    // Doppeltes (Artikel, Quelle)-Paar würde sich beim Bestandscheck gegenseitig
    // übersehen -> defensiv ablehnen (gleicher Artikel aus zwei Quellen ist ok).
    const duplicate = this.duplicateSourceKey(sourced);
    if (duplicate !== null) {
      return this.failure(
        `Artikel ${duplicate} ist mehrfach mit derselben Quelle vorhanden; je Quelle ist nur eine Position erlaubt.`,
        snapshot,
      );
    }

    // Verschrottung wird mit Bufferstock als MachineTrash in den
    // Bufferstock-Disposal gebucht, ohne Bufferstock (reine KFZ-Welt)
    // movement-los ins KFZ-Entsorgungslager - das steuert, welche Ressource die
    // Vorprüfung verlangt.
    const disposalToCar = targets.bufferstockId === undefined;
    const deltas = this.deltasFor(sourced, previous);

    if (this.needsBufferstock(deltas) && targets.bufferstockId === undefined) {
      return this.failure(
        $localize`Der Maschine ist kein Bufferstock-Lager zugeordnet; eine Buchung aus dem Bufferstock ist nicht möglich.`,
        snapshot,
      );
    }

    // Eine KFZ-Quelle - und in der reinen KFZ-Welt auch die Verschrottung -
    // braucht ein angemeldetes KFZ; ohne carId vor dem ersten Buchen ablehnen.
    if (this.needsCar(deltas, disposalToCar) && targets.carId === undefined) {
      return this.failure(
        $localize`Kein Fahrzeug angemeldet; eine Buchung aus dem KFZ ist nicht möglich.`,
        snapshot,
      );
    }

    // Alle Bewegungen dieser Befüllung teilen eine Transaction (ID = orderId),
    // damit der Auftrags-Abbruch sie über den Modul-Rollback zurücknehmen kann.
    const transaction = this.bookingService.transactionFor(targets.orderId);
    const movements = this.movementsFor(deltas, targets, transaction);

    // Validate-all: alle Bewegungen prüfen, alle Blocker sammeln. Nur wenn
    // keiner blockt, wird überhaupt gebucht.
    const prepared: Array<{
      movement: PositionMovement;
      request: BookingRequest;
      validation: Validation;
    }> = [];
    const blockers: string[] = [];
    for (const movement of movements) {
      const request = movement.buildRequest();
      const validation = await request.validate();
      if (validation.hasBlockers()) {
        blockers.push(validation.getBlockerMessage());
      } else {
        prepared.push({ movement, request, validation });
      }
    }
    if (blockers.length > 0) {
      return this.failure(blockers.join('\n'), snapshot);
    }

    // Submit in gelisteter Reihenfolge. Jede erfolgreiche Bewegung schreibt
    // ihren Stand in den Snapshot fort; ein erneuter book() mit diesem Snapshot
    // bucht sie nicht erneut (Differenz 0).
    for (const { movement, request, validation } of prepared) {
      const result = await request.submit(validation);
      if (!result.success) {
        return {
          success: false,
          message: result.message,
          retryable: result.retryable,
          snapshot,
        };
      }
      movement.commit(snapshot);
    }
    return { success: true, message: '', snapshot };
  }

  /**
   * Baut einen Snapshot aus dem geladenen Stand einer Positionsliste. Beim
   * Öffnen des Berichts sind die geladenen Positionen der zuletzt gebuchte Stand;
   * dieser Snapshot dient dann als Basis der Differenz beim nächsten Speichern.
   *
   * @param sourced Positionen im geladenen (zuletzt gebuchten) Stand
   * @returns Snapshot je Position
   */
  public snapshotFrom(sourced: SourcedFillPosition[]): FillSnapshot {
    const snapshot = new Map<string, FillSnapshotEntry>();
    for (const item of sourced) {
      snapshot.set(this.snapshotKey(item), {
        amount: item.position.amount ?? 0,
        takeBack: item.takeBack ?? 0,
        disposal: item.disposal ?? 0,
      });
    }
    return snapshot;
  }

  /**
   * Erste Artikel-Bezeichnung, deren (Artikel, Quelle)-Paar mehrfach vorkommt,
   * oder null. Gleicher Artikel aus verschiedenen Quellen ist erlaubt.
   */
  private duplicateSourceKey(sourced: SourcedFillPosition[]): string | null {
    const seen = new Set<string>();
    for (const item of sourced) {
      const key = this.snapshotKey(item);
      if (seen.has(key)) {
        return `${item.position.article_number ?? item.position.article_id}`;
      }
      seen.add(key);
    }
    return null;
  }

  /** Snapshot-Schlüssel einer Position: `${article_id}:${source}`. */
  private snapshotKey(item: SourcedFillPosition): string {
    return `${item.position.article_id}:${item.source}`;
  }

  /**
   * Zerlegt einen Snapshot-Schlüssel wieder in Artikel und Quelle. Für einen
   * verwaisten Eintrag (Quelle gewechselt oder Position entfernt) ist der
   * Schlüssel die einzige verbliebene Identität, aus der die Rückbuchung gebaut
   * wird. `source` enthält nie einen Doppelpunkt, `article_id` ist numerisch.
   */
  private identityFromKey(key: string): {
    articleId: number;
    source: MachineFillSource;
  } {
    const separator = key.lastIndexOf(':');
    return {
      articleId: Number(key.slice(0, separator)),
      source: key.slice(separator + 1) as MachineFillSource,
    };
  }

  /**
   * Differenz je Position gegen den zuletzt gebuchten Stand. Positionen ohne
   * Eintrag im Snapshot gelten als „noch nicht gebucht" (Vorstand 0), sodass die
   * erste Buchung die volle Menge als Differenz erhält.
   */
  private deltasFor(
    sourced: SourcedFillPosition[],
    previous: FillSnapshot,
  ): PositionDelta[] {
    const deltas = sourced.map(item => {
      const key = this.snapshotKey(item);
      const prev = previous.get(key);
      const net = (item.position.amount ?? 0) - (item.takeBack ?? 0);
      const prevNet = prev ? prev.amount - prev.takeBack : 0;
      return {
        item,
        key,
        deltaNet: net - prevNet,
        deltaDisposal: (item.disposal ?? 0) - (prev?.disposal ?? 0),
        fromCar: item.source === StockSourceType.CAR,
      };
    });

    // Verwaiste Snapshot-Einträge: früher gebuchte Positionen, die in den
    // aktuellen Positionen nicht mehr vorkommen (Quelle gewechselt oder Position
    // entfernt). Ihr gebuchter Stand wird gegen Null zurückgebucht (negatives
    // Netto = Rückgabe an die Quelle, Verschrottung als Storno).
    const currentKeys = new Set(deltas.map(delta => delta.key));
    for (const [key, prev] of previous) {
      if (currentKeys.has(key)) {
        continue;
      }
      const prevNet = prev.amount - prev.takeBack;
      if (prevNet === 0 && prev.disposal === 0) {
        continue;
      }
      const { articleId, source } = this.identityFromKey(key);
      deltas.push({
        item: {
          position: { article_id: articleId, amount: 0 },
          source,
          takeBack: 0,
          disposal: 0,
        },
        key,
        deltaNet: -prevNet,
        deltaDisposal: -prev.disposal,
        fromCar: source === StockSourceType.CAR,
      });
    }
    return deltas;
  }

  /** True, wenn eine Bufferstock-Quelle eine Füll-Differenz erzeugt. */
  private needsBufferstock(deltas: PositionDelta[]): boolean {
    return deltas.some(
      delta =>
        delta.item.source === StockSourceType.BUFFERSTOCK && delta.deltaNet !== 0,
    );
  }

  /**
   * True, wenn das KFZ gebraucht wird: eine KFZ-Quelle mit Füll-Differenz oder -
   * ohne Bufferstock - eine Disposal-Differenz, die movement-los ins
   * KFZ-Entsorgungslager läuft.
   */
  private needsCar(deltas: PositionDelta[], disposalToCar: boolean): boolean {
    return deltas.some(
      delta =>
        (delta.fromCar && delta.deltaNet !== 0) ||
        (disposalToCar && delta.deltaDisposal !== 0),
    );
  }

  /**
   * Bildet je Positions-Differenz die zu buchenden Bewegungen: die Füll-Differenz
   * (`deltaNet`) als CarTransfer (KFZ) bzw. OrderMovement (Bufferstock), die
   * Disposal-Differenz (`deltaDisposal`) als MachineTrash (mit Bufferstock)
   * bzw. movement-los ins KFZ-Entsorgungslager. Differenz 0 emittiert nichts;
   * eine negative Differenz bucht die Rückgabe an die Quelle.
   */
  private movementsFor(
    deltas: PositionDelta[],
    targets: MachineFillTargets,
    transaction: BookingTransaction,
  ): PositionMovement[] {
    const { machineId, carId, bufferstockId, description = '', orderNumber } =
      targets;
    // Alle Bewegungen dieser Befüllung teilen die auftragsbezogene Referenz.
    const reference = BookingReference.forOrder(orderNumber);
    const movements: PositionMovement[] = [];
    for (const delta of deltas) {
      const { item, key, deltaNet, deltaDisposal, fromCar } = delta;
      const { position } = item;

      if (deltaNet !== 0) {
        movements.push({
          buildRequest: () => {
            const builder = this.bookingService
              .bookingOf([{ ...position, amount: deltaNet }])
              .withDescription(description)
              .withReference(reference)
              .withTransaction(transaction);
            return fromCar
              ? // tslint:disable-next-line:no-non-null-assertion book()-Guard sichert carId
                builder.fromCar(carId!).toMachine(machineId)
              : // tslint:disable-next-line:no-non-null-assertion book()-Guard sichert bufferstockId
                builder.fromBufferstock(bufferstockId!).toMachine(machineId);
          },
          commit: snapshot => {
            const entry = snapshot.get(key) ?? this.emptyEntry();
            this.writeOrPrune(snapshot, key, {
              ...entry,
              amount: position.amount ?? 0,
              takeBack: item.takeBack ?? 0,
            });
          },
        });
      }

      if (deltaDisposal !== 0) {
        movements.push({
          buildRequest: () => {
            const builder = this.bookingService
              .bookingOf([{ ...position, amount: deltaDisposal }])
              .withDescription(description)
              .withReference(reference)
              .withTransaction(transaction);
            // Mit Bufferstock: Verschrottung als MachineTrash in den
            // Bufferstock-Disposal. Ohne (reine KFZ-Welt): movement-los ins
            // KFZ-Entsorgungslager.
            return bufferstockId !== undefined
              ? builder
                  .fromMachine(machineId)
                  .toBufferstockDisposal(bufferstockId)
              : // tslint:disable-next-line:no-non-null-assertion needsCar-Guard sichert carId
                builder.fromCar(carId!).toTrashBin();
          },
          commit: snapshot => {
            const entry = snapshot.get(key) ?? this.emptyEntry();
            this.writeOrPrune(snapshot, key, {
              ...entry,
              disposal: item.disposal ?? 0,
            });
          },
        });
      }
    }
    return movements;
  }

  private emptyEntry(): FillSnapshotEntry {
    return { amount: 0, takeBack: 0, disposal: 0 };
  }

  /**
   * Schreibt den Eintrag fort oder entfernt ihn, wenn er komplett leer ist. Das
   * Entfernen hält den Snapshot bei wiederholtem Quellenwechsel stabil: ein
   * Null-Eintrag wird beim nächsten Speichern nicht erneut als verwaist erkannt.
   */
  private writeOrPrune(
    snapshot: Map<string, FillSnapshotEntry>,
    key: string,
    entry: FillSnapshotEntry,
  ): void {
    if (entry.amount === 0 && entry.takeBack === 0 && entry.disposal === 0) {
      snapshot.delete(key);
    } else {
      snapshot.set(key, entry);
    }
  }

  private failure(
    message: string,
    snapshot: FillSnapshot,
  ): MachineFillBookingResult {
    return { success: false, message, snapshot };
  }
}
